import { split, ApolloLink } from '@apollo/client/link'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client'

type CreateApolloClientProps = {
  uri: string
  credentials?: RequestCredentials
  getToken?: () => string | null
}

export function createApolloClient({
  uri,
  credentials = 'same-origin',
  getToken,
}: CreateApolloClientProps) {
  const httpLink = new HttpLink({ uri, credentials })

  const authLink = new ApolloLink((operation, forward) => {
    const token = getToken?.()
    if (token) {
      operation.setContext(
        ({ headers = {} }: { headers?: Record<string, string> }) => ({
          headers: { ...headers, Authorization: `Bearer ${token}` },
        }),
      )
    }
    return forward(operation)
  })

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
  })
}

type CreateSubscriptionApolloClientProps = {
  httpUri: string
  wsUri?: string
  credentials?: RequestCredentials
  subscriptionLink?: ApolloLink
  getToken?: () => string | null
}

export function createSubscriptionApolloClient({
  httpUri,
  wsUri,
  credentials = 'same-origin',
  subscriptionLink,
  getToken,
}: CreateSubscriptionApolloClientProps) {
  const baseHttpLink = new HttpLink({ uri: httpUri, credentials })

  const authLink = new ApolloLink((operation, forward) => {
    const token = getToken?.()
    if (token) {
      operation.setContext(
        ({ headers = {} }: { headers?: Record<string, string> }) => ({
          headers: { ...headers, Authorization: `Bearer ${token}` },
        }),
      )
    }
    return forward(operation)
  })

  const httpLink = getToken ? authLink.concat(baseHttpLink) : baseHttpLink

  const wsOrCustomLink: ApolloLink =
    subscriptionLink ?? new GraphQLWsLink(createClient({ url: wsUri ?? '' }))

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      )
    },
    wsOrCustomLink,
    httpLink,
  )

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: splitLink,
    cache: new InMemoryCache(),
  })
}
